import { type ReactNode } from 'react';
import { Box, Divider as MuiDivider, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

/** Shared analytics palette (used with MUI sx). */
export const AnalyticsColors = {
    primary: '#6366f1',
    primaryLight: '#EEF2FF',
    primaryMuted: '#818CF8',
    secondary: '#2563EB',
    secondaryLight: '#DBEAFE',
    success: '#059669',
    successLight: '#D1FAE5',
    successBorder: '#34D399',
    warning: '#D97706',
    warningLight: '#FEF3C7',
    warningBorder: '#FBBF24',
    danger: '#DC2626',
    dangerLight: '#FEE2E2',
    dangerBorder: '#F87171',
    darkRed: '#991B1B',
    cardBg: '#FFFFFF',
    borderLight: 'rgba(148, 163, 184, 0.28)',
    borderMedium: '#D1D5DB',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    textTertiary: '#94a3b8',
    surfaceBg: '#f8fafc',
    surfaceElevated: '#FFFFFF',
} as const;

export const sectionCardSx: SxProps<Theme> = {
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    p: { xs: 1.75, sm: 2.25 },
    mb: 2,
    boxShadow: 'none',
};

export const sectionTitleSx: SxProps<Theme> = {
    fontSize: 15,
    fontWeight: 700,
    color: 'text.primary',
    m: '0 0 1.25rem 0',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    letterSpacing: '-0.01em',
    '&::before': {
        content: '""',
        width: 3,
        height: 16,
        bgcolor: 'primary.main',
        borderRadius: '2px',
        flexShrink: 0,
    },
};

export const stateMessageSx: SxProps<Theme> = {
    py: 3,
    textAlign: 'center',
    color: 'text.secondary',
    fontSize: 13,
};

export const loadingWrapSx: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 4,
};

export const showMoreBtnSx: SxProps<Theme> = {
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: 600,
    color: 'primary.main',
};

export const thSx = (opts?: {
    align?: 'left' | 'right' | 'center';
    sortable?: boolean;
    active?: boolean;
}): SxProps<Theme> => ({
    textAlign: opts?.align ?? 'left',
    fontSize: 11,
    fontWeight: 600,
    color: opts?.active ? 'primary.main' : 'text.secondary',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    p: '0.5rem 0.75rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
    cursor: opts?.sortable ? 'pointer' : 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
        color: opts?.sortable ? 'primary.main' : 'text.secondary',
    },
});

export const tdSx = (opts?: {
    align?: 'left' | 'right' | 'center';
    bold?: boolean;
}): SxProps<Theme> => ({
    fontSize: '13px',
    color: 'text.primary',
    fontWeight: opts?.bold ? 600 : 400,
    p: '0.6rem 0.75rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
    textAlign: opts?.align ?? 'left',
    fontVariantNumeric: 'tabular-nums',
    verticalAlign: 'middle',
});

export const trHoverSx: SxProps<Theme> = {
    transition: 'background 0.1s',
    '&:hover': { bgcolor: 'action.hover' },
    '&:last-child td': { borderBottom: 'none' },
};

export const toggleGroupSx: SxProps<Theme> = {
    display: 'inline-flex',
    flexWrap: 'wrap',
    bgcolor: 'action.hover',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '10px',
    p: '3px',
    gap: '2px',
    width: { xs: '100%', sm: 'fit-content' },
};

export const toggleBtnSx = (active: boolean): SxProps<Theme> => ({
    bgcolor: active ? 'background.paper' : 'transparent',
    color: active ? 'primary.main' : 'text.secondary',
    border: 'none',
    borderRadius: '8px',
    px: { xs: 1.25, sm: 1.75 },
    py: 0.75,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    flex: { xs: '1 1 auto', sm: '0 0 auto' },
    boxShadow: active ? '0 1px 2px rgba(15, 23, 42, 0.06)' : 'none',
    '&:hover': { color: 'primary.main' },
    '&:disabled': { cursor: 'not-allowed', opacity: 0.7 },
});

interface ChildrenProps {
    children: ReactNode;
    sx?: SxProps<Theme>;
}

export const SectionCard = ({ children, sx }: ChildrenProps) => (
    <Box sx={[sectionCardSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])] as SxProps<Theme>}>
        {children}
    </Box>
);

export const SectionTitle = ({ children }: ChildrenProps) => (
    <Typography variant="h2" sx={sectionTitleSx}>
        {children}
    </Typography>
);

export const SectionSubtitle = ({ children }: ChildrenProps) => (
    <Typography
        sx={{
            fontSize: 13,
            color: 'text.secondary',
            mt: '-0.75rem',
            mb: 1.5,
            pl: '11px',
            lineHeight: 1.5,
        }}
    >
        {children}
    </Typography>
);

interface GridProps extends ChildrenProps {
    columns?: number;
    gap?: string | number;
}

export const Grid = ({ children, columns = 2, gap = 2 }: GridProps) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: `repeat(${columns}, 1fr)` },
            gap,
        }}
    >
        {children}
    </Box>
);

interface FlexRowProps extends ChildrenProps {
    gap?: string | number;
    align?: string;
    justify?: string;
}

export const FlexRow = ({
    children,
    gap = 1.5,
    align = 'center',
    justify = 'flex-start',
}: FlexRowProps) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: align,
            justifyContent: justify,
            gap,
            flexWrap: 'wrap',
        }}
    >
        {children}
    </Box>
);

export const Divider = () => <MuiDivider sx={{ my: 2, borderColor: 'divider' }} />;
